import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Calendar,
  Building2,
  Briefcase,
  Download,
  Plus,
} from "lucide-react";
import { motion } from "framer-motion";
import useAuth from "../../../contexts/AuthContext";
import Loader from "../../../common_components/Loader";
import { ApplicationStatus, matchStatus } from "../../../types/status";

type StatCardColor = "blue" | "gray" | "yellow" | "green" | "red";

interface StatCardProps {
  label: string;
  value: number;
  color: StatCardColor;
  active: boolean;
  onClick: () => void;
}

export default function MyAlliedApplications() {
  const { admissionId } = useParams();
  const [applications, setApplications] = useState<any[]>([]);
  const [admissionDetails, setAdmissionDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const { apiRequest } = useAuth();
  const navigate = useNavigate();
  const fetchedRef = useRef(false);

  const fetchAdmissionDetails = useCallback(async () => {
    if (!admissionId) return;
    try {
      const res = await apiRequest(`/admission-vacancies/${admissionId}`);
      if (res?.success && res.data) {
        setAdmissionDetails(res.data);
      }
    } catch (error) {
      console.error("Error fetching admission details:", error);
    }
  }, [apiRequest, admissionId]);

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const url = admissionId
        ? `/applications/my-applications?admissionId=${admissionId}`
        : "/applications/my-applications";
      const res = await apiRequest(url, { method: "GET" });
      setApplications(res || []);
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setLoading(false);
    }
  }, [apiRequest, admissionId]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchApplications();
    if (admissionId) {
      fetchAdmissionDetails();
    }
  }, [admissionId, fetchAdmissionDetails, fetchApplications]);

  const getStatusConfig = (status: string) => {
    if (matchStatus(status, ApplicationStatus.ACCEPTED)) {
      return {
        label: "Accepted",
        icon: CheckCircle,
        bgColor: "bg-green-100",
        textColor: "text-green-700",
      };
    }
    if (matchStatus(status, ApplicationStatus.REJECTED)) {
      return {
        label: "Rejected",
        icon: XCircle,
        bgColor: "bg-red-100",
        textColor: "text-red-700",
      };
    }
    if (matchStatus(status, ApplicationStatus.SUBMITTED)) {
      return {
        label: "Submitted",
        icon: CheckCircle,
        bgColor: "bg-blue-100",
        textColor: "text-blue-700",
      };
    }
    return {
      label: "Pending",
      icon: Clock,
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-800",
    };
  };

  const formatDate = (date?: string | Date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const filteredApplications = applications.filter((app: any) => {
    if (filter === "ALL") return true;
    if (filter === "DRAFT") return matchStatus(app.status, ApplicationStatus.DRAFT);
    if (filter === "SUBMITTED") return matchStatus(app.status, ApplicationStatus.SUBMITTED);
    if (filter === "ACCEPTED") return matchStatus(app.status, ApplicationStatus.ACCEPTED);
    if (filter === "REJECTED") return matchStatus(app.status, ApplicationStatus.REJECTED);
    return true;
  });

  const stats = {
    total: applications.length,
    draft: applications.filter(
      (a: any) => matchStatus(a.status, ApplicationStatus.DRAFT)
    ).length,
    submitted: applications.filter(
      (a: any) => matchStatus(a.status, ApplicationStatus.SUBMITTED)
    ).length,
    accepted: applications.filter(
      (a: any) => matchStatus(a.status, ApplicationStatus.ACCEPTED)
    ).length,
    rejected: applications.filter(
      (a: any) => matchStatus(a.status, ApplicationStatus.REJECTED)
    ).length,
  };

  return (
    <div className="flex-grow bg-slate-50 font-sans rounded-xl p-4 sm:p-6 w-full">
      <div className="max-w-7xl mx-auto">
        <header className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-serif font-bold text-slate-900">
              {admissionDetails ? admissionDetails.title || admissionDetails.name : "My Allied Health Sciences Applications"}
            </h1>
            <p className="text-slate-500 mt-1">
              {admissionDetails
                ? `Track and manage your applications for ${admissionDetails.title || admissionDetails.name}`
                : "Track and manage your submitted applications for Allied Health Sciences programs"}
            </p>
          </div>
          {admissionId && (
            <button
              onClick={() => navigate(`/allied/admission/${admissionId}/apply`)}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-slate-900 text-white rounded-xl shadow-md hover:bg-slate-800 transition-all font-semibold text-sm flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
              Apply For Admission
            </button>
          )}
        </header>

        {/* Statistics Filter Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard
            label="Total"
            value={stats.total}
            color="blue"
            active={filter === "ALL"}
            onClick={() => setFilter("ALL")}
          />
          <StatCard
            label="Draft"
            value={stats.draft}
            color="gray"
            active={filter === "DRAFT"}
            onClick={() => setFilter("DRAFT")}
          />
          <StatCard
            label="Submitted"
            value={stats.submitted}
            color="blue"
            active={filter === "SUBMITTED"}
            onClick={() => setFilter("SUBMITTED")}
          />
          <StatCard
            label="Accepted"
            value={stats.accepted}
            color="green"
            active={filter === "ACCEPTED"}
            onClick={() => setFilter("ACCEPTED")}
          />
          <StatCard
            label="Rejected"
            value={stats.rejected}
            color="red"
            active={filter === "REJECTED"}
            onClick={() => setFilter("REJECTED")}
          />
        </div>

        {/* Loading State */}
        {loading && <Loader text="Loading your applications..." />}

        {/* Applications List */}
        {!loading && (
          <>
            {filteredApplications.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16 rounded-xl bg-white shadow-xs border border-slate-200"
              >
                <div className="inline-block p-6 bg-slate-50 rounded-full mb-4">
                  <FileText className="w-16 h-16 text-slate-400" />
                </div>
                <h3 className="text-xl font-serif font-bold text-slate-700 mb-2">
                  {filter === "ALL"
                    ? "No Applications Found"
                    : `No ${filter.toLowerCase()} applications`}
                </h3>
                <p className="text-slate-500 mb-6">
                  {filter === "ALL"
                    ? "You have not submitted any applications for this admission vacancy yet."
                    : "Try selecting a different filter tab above."}
                </p>
                {admissionId ? (
                  <button
                    onClick={() => navigate(`/allied/admission/${admissionId}/apply`)}
                    className="px-6 py-3 bg-slate-800 text-white rounded-xl shadow hover:bg-slate-900 transition-all font-medium border border-slate-700 inline-flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" /> Apply For Admission
                  </button>
                ) : (
                  <button
                    onClick={() => navigate("/allied")}
                    className="px-6 py-3 bg-slate-800 text-white rounded-xl shadow hover:bg-slate-900 transition-all font-medium border border-slate-700"
                  >
                    Browse Admission Vacancies
                  </button>
                )}
              </motion.div>
            ) : (
              <div className="space-y-6">
                {filteredApplications.map((app: any, index: number) => {
                  const statusConfig = getStatusConfig(app.status);
                  const StatusIcon = statusConfig.icon;
                  const isDraft = matchStatus(app.status, ApplicationStatus.DRAFT);

                  const prefText = Array.isArray(app.preferenceOrder)
                    ? app.preferenceOrder.join(", ")
                    : app.preferenceOrder || "N/A";

                  return (
                    <motion.div
                      key={app.applicationId || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white rounded-xl shadow-xs hover:shadow-md transition-all duration-300 overflow-hidden border border-slate-200"
                    >
                      <div className="p-6">
                        {/* Header Row */}
                        <div className="flex items-start justify-between mb-6">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="text-lg font-bold text-slate-900">
                                {app.candidateName || "Applicant"}
                              </h3>
                              <span
                                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${statusConfig.bgColor} ${statusConfig.textColor}`}
                              >
                                <StatusIcon className="w-3.5 h-3.5" />
                                {statusConfig.label}
                              </span>
                            </div>
                            <p className="text-sm text-slate-500">
                              Application ID:{" "}
                              <span className="font-mono font-semibold text-slate-800">
                                {app.applicationId}
                              </span>
                            </p>
                          </div>
                        </div>

                        {/* Details Grid */}
                        <div className="grid md:grid-cols-3 gap-4 mb-6 bg-slate-50 p-4 rounded-xl">
                          <div className="flex items-start gap-3 min-w-0 md:col-span-3 pb-3 border-b border-slate-200/60">
                            <div className="p-2 bg-blue-100/70 rounded-lg flex-shrink-0 mt-0.5">
                              <Building2 className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">
                                Course Preferences
                              </p>
                              {Array.isArray(app.preferenceOrder) && app.preferenceOrder.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                  {app.preferenceOrder.map((pref: string, idx: number) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 shadow-2xs break-words max-w-full leading-snug"
                                    >
                                      <span className="text-blue-600 font-bold">{idx + 1}.</span> {pref}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm font-semibold text-slate-800 break-words whitespace-normal leading-snug">
                                  {prefText}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-100/70 rounded-lg flex-shrink-0">
                              <Briefcase className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">
                                NEET Details
                              </p>
                              <p className="text-sm font-semibold text-slate-800">
                                Roll: {app.neetRoll || "N/A"}{" "}
                                {app.neetAIRank ? `(AIR: ${app.neetAIRank})` : ""}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100/70 rounded-lg flex-shrink-0">
                              <Calendar className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">
                                Applied On
                              </p>
                              <p className="text-sm font-semibold text-slate-800">
                                {formatDate(app.submissionDate || app.createdAt)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-3">
                          {isDraft ? (
                            <button
                              onClick={() =>
                                navigate(
                                  `/allied/admission/${app.admissionId}/apply/${app.applicationId}`
                                )
                              }
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 text-white rounded-xl font-medium hover:bg-slate-900 transition-all shadow-xs"
                            >
                              <Edit className="w-4 h-4" />
                              Continue Application
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() =>
                                  navigate(`/allied/application/${app.applicationId}/view`)
                                }
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 text-white rounded-xl font-medium hover:bg-slate-900 transition-colors shadow-xs"
                              >
                                <Eye className="w-4 h-4" />
                                View Details
                              </button>
                              <a
                                href={`/allied/application/${app.applicationId}/print`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
                              >
                                <Download className="w-4 h-4" />
                                Download / Print
                              </a>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Timeline Footer */}
                      <div className="bg-slate-50/60 px-6 py-3.5 border-t border-slate-100">
                        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                          <div>
                            <span className="font-medium text-slate-400">Category:</span>{" "}
                            {app.category || "UR"}
                          </div>
                          <div>
                            <span className="font-medium text-slate-400">Payment:</span>{" "}
                            {app.payment?.status || "N/A"}{" "}
                            {app.payment?.utr ? `(${app.payment.utr})` : ""}
                          </div>
                          <div>
                            <span className="font-medium text-slate-400">Applied:</span>{" "}
                            {formatDate(app.submissionDate || app.createdAt)}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color, active, onClick }: StatCardProps) {
  const colorClasses: Record<StatCardColor, string> = {
    blue: active
      ? "bg-blue-600 text-white shadow-md shadow-blue-100"
      : "bg-white text-blue-700 hover:bg-blue-50/50",
    gray: active
      ? "bg-slate-800 text-white shadow-md shadow-slate-200"
      : "bg-white text-slate-700 hover:bg-slate-50/50",
    yellow: active
      ? "bg-amber-600 text-white shadow-md shadow-amber-100"
      : "bg-white text-amber-700 hover:bg-amber-50/50",
    green: active
      ? "bg-green-600 text-white shadow-md shadow-green-100"
      : "bg-white text-green-700 hover:bg-green-50/50",
    red: active
      ? "bg-red-600 text-white shadow-md shadow-red-100"
      : "bg-white text-red-700 hover:bg-red-50/50",
  };

  return (
    <motion.button
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={onClick}
      className={`p-4 rounded-xl shadow-xs border border-slate-200 transition-all cursor-pointer text-left ${colorClasses[color]}`}
    >
      <p className={`text-2xl font-bold mb-1 ${active ? "text-white" : "text-slate-900"}`}>
        {value}
      </p>
      <p className={`text-xs font-semibold ${active ? "text-white/80" : "text-slate-500"}`}>
        {label}
      </p>
    </motion.button>
  );
}
